// Group-level presentation + name→group bucketing. This module owns NO
// per-tool data — per-tool description and group come from the backend
// catalog (GET /chat/tool-catalog). The only tool-related data the frontend
// owns is the display title/description/icon for the small, stable set of
// group keys below.
import type { CatalogTool } from '@/lib/api/toolSelection'

export const CATEGORY_INFO: Record<
  string,
  { title: string; description: string; icon: string }
> = {
  core: {
    title: 'Core LCA Tools',
    description: 'Essential tools for Life Cycle Assessment analysis',
    icon: 'Bot',
  },
  graph: {
    title: 'Graph Analysis',
    description: 'Advanced Neo4j database queries for supply chain analysis',
    icon: 'Database',
  },
  usability: {
    title: 'Usability & Validation',
    description: 'Helper tools for workflow efficiency',
    icon: 'Zap',
  },
  management: {
    title: 'Project Management',
    description: 'Create and manage custom LCA projects and activities',
    icon: 'Settings',
  },
  reporting: {
    title: 'Reports',
    description: 'Create, write, validate and finalize deterministic LCA reports',
    icon: 'FileText',
  },
  other: {
    title: 'Other Tools',
    description: 'Additional tools discovered from connected servers',
    icon: 'Wrench',
  },
}

/** Canonical group ordering for display. */
export const GROUP_ORDER: string[] = [
  'core',
  'graph',
  'usability',
  'management',
  'reporting',
  'other',
]

/**
 * Order group keys for display: known groups in GROUP_ORDER order (only those
 * present), followed by any unknown groups sorted alphabetically. Deduplicated.
 */
export function orderGroups(present: Iterable<string>): string[] {
  const set = new Set(present)
  const known = GROUP_ORDER.filter((g) => set.has(g))
  const unknown = [...set].filter((g) => !GROUP_ORDER.includes(g)).sort()
  return [...known, ...unknown]
}

/**
 * Bucket tool names by their catalog group. Names absent from `catalogByName`
 * are omitted (unreachable → not shown). Groups with no tools do not appear in
 * the returned map.
 */
export function groupToolNames(
  names: Iterable<string>,
  catalogByName: Map<string, CatalogTool>,
): Map<string, CatalogTool[]> {
  const out = new Map<string, CatalogTool[]>()
  for (const name of names) {
    const tool = catalogByName.get(name)
    if (!tool) continue
    const arr = out.get(tool.group) ?? []
    arr.push(tool)
    out.set(tool.group, arr)
  }
  return out
}
